// Pestañas del workspace de un pedido.
//
// Antes esto hacía scroll a una sección que seguía montada más abajo, y en un
// pedido de 83 líneas y 18 cargas la página medía 7.000 px: el mismo problema
// que la pantalla vino a resolver, sólo que con atajos. Ahora cambia de módulo:
// se monta UNA sección, y la altura de la página deja de crecer con la suma de
// todo lo que contiene el pedido.
//
// Los contadores se quedan aunque la sección no esté a la vista: son la única
// forma de seguir sabiendo el tamaño del pedido desde cualquier pestaña.
import { rotuloCls } from './estilos'
import type { TabPedido } from './useTabsPedido'

export interface DefinicionTab {
  id: TabPedido
  etiqueta: string
  /** Volumen de la sección. `undefined` = esta pestaña no cuenta nada. */
  cuenta?: number
}

interface Props {
  dark: boolean
  tabs: DefinicionTab[]
  activa: TabPedido
  onCambio: (tab: TabPedido) => void
}

export function TabsPedido({ dark, tabs, activa, onCambio }: Props) {
  return (
    <div
      role="tablist"
      aria-label="Secciones del pedido"
      className={`sticky top-0 z-20 -mx-6 px-6 flex items-center gap-1 overflow-x-auto scroll-thin border-b ${
        dark ? 'border-white/8 bg-slate-950/85' : 'border-slate-200 bg-white/85'
      } backdrop-blur-md`}
    >
      {tabs.map(t => {
        const esActiva = t.id === activa
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={esActiva}
            onClick={() => onCambio(t.id)}
            // El subrayado va en el propio botón y no en un indicador aparte:
            // menos piezas móviles y el foco de teclado cae donde debe.
            className={`relative flex items-center gap-1.5 px-3 py-2.5 text-[13px] whitespace-nowrap transition-colors duration-150 cursor-pointer border-b-2 -mb-px ${
              esActiva
                ? dark
                  ? 'border-sky-400 text-slate-50 font-semibold'
                  : 'border-sky-600 text-slate-900 font-semibold'
                : dark
                  ? 'border-transparent text-slate-400 font-medium hover:text-slate-200 hover:border-white/15'
                  : 'border-transparent text-slate-500 font-medium hover:text-slate-800 hover:border-slate-300'
            }`}
          >
            {t.etiqueta}
            {/* El badge mantiene siempre el mismo peso: lo que distingue a la
                pestaña activa es el subrayado y el color del texto. */}
            {t.cuenta !== undefined && (
              <span className={`text-[11px] tabular-nums font-semibold px-1.5 py-0.5 rounded transition-colors duration-150 ${
                esActiva
                  ? dark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-50 text-sky-700'
                  : dark ? 'bg-white/[0.06] text-slate-500' : 'bg-slate-100 text-slate-500'
              }`}>
                {t.cuenta}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/** Cabecera de una sección dentro de una pestaña.
 *
 *  Más discreta que la de la versión anterior: la pestaña activa ya dice dónde
 *  está el usuario, así que aquí sólo hacen falta las acciones. */
export function BarraSeccion({
  dark, titulo, cuenta, children,
}: {
  dark: boolean
  titulo?: string
  cuenta?: number
  children?: React.ReactNode
}) {
  if (!titulo && !children) return null
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {titulo && <h2 className={rotuloCls(dark)}>{titulo}</h2>}
      {cuenta !== undefined && (
        <span className={`text-[11px] tabular-nums font-bold ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
          {cuenta}
        </span>
      )}
      {children && <div className="ml-auto flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  )
}
